
import React, { useState, useMemo, useEffect } from 'react';
import * as Firestore from 'firebase/firestore';
import { db } from '../services/firebase';
import type { MealPlan, Recipe, DayOfWeek, Language, MealType, PlannedMeal, Role, HouseholdRole, WeekStartDay, ActiveShoppingCart, ShoppingCategory, UserMealPreferences, Chef, User, Household } from '../types';
import { DAYS_OF_WEEK } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import ChevronUpIcon from './icons/ChevronUpIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import XIcon from './icons/XIcon';
import LayoutGridIcon from './icons/LayoutGridIcon';
import ListIcon from './icons/ListIcon';
import CartIcon from './icons/CartIcon';
import CalendarIcon from './icons/CalendarIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import DayCard from './DayCard';
import RecipeSelectorModal from './RecipeSelectorModal';
import BulkShoppingListModal from './BulkShoppingListModal';
import CompactWeekSelector from './CompactWeekSelector';
import PlanOverviewCard from './PlanOverviewCard';
import RecipeUsageModal from './RecipeUsageModal';
import { calculateMonthlyMeat } from '../utils/meatCalculator';
import { MEAL_ORDER, normalizeVisibleMealTypes } from '../utils/mealScheduleDefaults';
import { attachRecipeToPlannedMeal } from '../utils/plannedMealRecipe';
import { useCookingSessionStatuses } from '../hooks/useCookingSessionStatuses';
import PlannerHeader from './PlannerHeader';

interface MonthlyPlannerProps {
    viewMode: 'week' | 'month';
    setViewMode: (mode: 'week' | 'month') => void;
    onInfoClick?: () => void;
    activeHouseholdId?: string;
    activeHousehold?: Household | null;
    recipes: Recipe[];
    language?: Language;
    onGoToWeek: (offset: number) => void;
    role: Role;
    householdRole: HouseholdRole;
    onAddRecipesToDay: (recipeIds: string[], day: DayOfWeek, mealType: MealType, weekOffset: number) => void;
    onScheduleMeal?: (recipeId: string, day: DayOfWeek, mealType: MealType, servings: number, status: 'buy' | 'pantry' | 'prepared', weekOffset: number) => Promise<void> | void;
    onRemoveRecipeFromDay: (plannedMealId: string, day: DayOfWeek, weekOffset: number) => void;
    onMoveMeal: (plannedMealId: string, fromDay: DayOfWeek, toDay: DayOfWeek, toType: MealType, weekOffset: number) => void;
    onUpdateMeal: (plannedMealId: string, day: DayOfWeek, updates: { servings?: number; status?: 'buy' | 'pantry' | 'prepared' }, weekOffset: number) => void;
    onRecipeClick: (recipe: Recipe) => void;
    onCookMeal: (meals: (PlannedMeal & { recipe: Recipe })[], type: MealType, day?: DayOfWeek, weekOffset?: number, slotMenuTitles?: Partial<Record<string, string>>, initialRecipeId?: string) => void;
    onUpdateMealSlotTitle?: (day: DayOfWeek, mealType: MealType, title: string | null, weekOffset?: number) => void;
    visibleMealTypes?: MealType[];
    weekStartDay?: WeekStartDay;
    activeCart?: ActiveShoppingCart | null;
    onUpdateActiveCart?: (cart: ActiveShoppingCart) => Promise<void>;
    onEditMeal?: (meal: PlannedMeal & { recipe: Recipe }, day: DayOfWeek, weekOffset: number) => void;
    onToggleFavoriteRecipe?: (recipeId: string) => void;
    onCreateRecipe?: (name: string) => Promise<string | void>;
    categoryOverrides?: Record<string, ShoppingCategory>;
    currentUserId?: string;
    userMealPreferences?: UserMealPreferences | null;
    chefs?: Chef[];
    allUsers?: User[];
}

const { collection, query, where, onSnapshot } = Firestore as any;

const formatDateLocal = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getStartOfWeekLocal = (date: Date, startDay: WeekStartDay = 'Monday'): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    let diff;
    if (startDay === 'Sunday') {
        diff = d.getDate() - day;
    } else {
        const dayIndex = day === 0 ? 6 : day - 1;
        diff = d.getDate() - dayIndex;
    }
    const result = new Date(d);
    result.setDate(diff);
    return result;
};

const MealPill: React.FC<{ 
    type: 'morning' | 'noon' | 'evening'; 
    count: number; 
    isActiveFilter: boolean; 
}> = ({ type, count, isActiveFilter }) => {
    if (!isActiveFilter) return null;
    
    if (count === 0) {
        return <div className="h-5 min-w-[1.75rem] rounded-md border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30" />;
    }

    const colors = {
        morning: "bg-yellow-100 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800/50",
        noon: "bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800/50",
        evening: "bg-slate-700 text-white border border-slate-800 shadow-sm dark:bg-slate-600 dark:border-slate-500"
    };

    return (
        <div className={`text-[10px] font-black min-w-[1.75rem] h-5 px-1 rounded-md flex items-center justify-center transition-all ${colors[type]}`}>
            {count}
        </div>
    );
};

const WeekRow: React.FC<{
    week: Date[];
    index: number;
    currentMonth: Date;
    calculateWeekOffset: (d: Date) => number;
    getMealCounts: (d: Date) => { breakfast: number, lunch: number, dinner: number };
    setInspectingDay: (val: { date: Date, weekOffset: number }) => void;
    onOpenShoppingList: (dates: Date[]) => void;
    onViewWeek: (offset: number) => void;
    visibleMeals: MealType[];
    t: (k: any) => string;
}> = ({ week, index, currentMonth, calculateWeekOffset, getMealCounts, setInspectingDay, onOpenShoppingList, onViewWeek, visibleMeals, t }) => {
    const isThisWeek = useMemo(() => week.some(d => d.toDateString() === new Date().toDateString()), [week]);
    const [isExpanded, setIsExpanded] = useState(isThisWeek);
    
    const weekStart = week[0];
    const weekEnd = week[6];
    const formatDate = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
    const offset = calculateWeekOffset(weekStart);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col transition-all duration-300">
            <div 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex justify-between items-center px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 hover:bg-sky-50 dark:hover:bg-slate-800 cursor-pointer transition-colors group"
            >
                <div className="flex items-center gap-3">
                    <h3 className="font-black text-slate-800 dark:text-slate-200 text-xs uppercase tracking-widest">
                        {t('weekThis')} {index + 1}
                    </h3>
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                        {formatDate(weekStart)} — {formatDate(weekEnd)}
                    </span>
                    {isThisWeek && (
                        <span className="px-2 py-0.5 bg-brand-orange/10 text-brand-orange rounded-full text-[8px] font-black uppercase tracking-tighter">{t('currentWeekBadge')}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={(e) => { e.stopPropagation(); onViewWeek(offset); }}
                        className="p-2 bg-white dark:bg-slate-700 border border-slate-100 dark:border-slate-600 text-slate-400 dark:text-slate-300 hover:text-brand-orange hover:border-brand-orange dark:hover:border-brand-orange rounded-xl shadow-sm transition-all"
                        title={t('viewFullWeek')}
                    >
                        <CalendarIcon className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); onOpenShoppingList(week); }}
                        className="p-2 bg-white dark:bg-slate-700 border border-slate-100 dark:border-slate-600 text-slate-400 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 hover:border-sky-300 dark:hover:border-sky-500 rounded-xl shadow-sm transition-all"
                        title={t('shoppingList')}
                    >
                        <CartIcon className="w-4 h-4" />
                    </button>
                    <div className="text-slate-300 dark:text-slate-600 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors ml-2">
                        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </div>
                </div>
            </div>

            {isExpanded ? (
                <div className="grid grid-cols-7 divide-x divide-slate-100 dark:divide-slate-800 rtl:divide-x-reverse border-b border-slate-100 dark:border-slate-800 animate-fade-in bg-white dark:bg-slate-900">
                    {week.map((date, dIdx) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isDifferentMonth = date.getMonth() !== currentMonth.getMonth();
                        const mealCounts = getMealCounts(date);

                        return (
                            <div 
                                key={dIdx} 
                                onClick={(e) => { e.stopPropagation(); setInspectingDay({ date, weekOffset: offset }); }}
                                className={`flex flex-col min-h-[110px] py-2 px-1 cursor-pointer transition-all hover:bg-slate-50/80 dark:hover:bg-slate-800/80 group relative ${isToday ? 'bg-amber-50/20 dark:bg-amber-900/10 ring-2 ring-inset ring-brand-orange z-10' : ''} ${isDifferentMonth ? 'opacity-30 bg-slate-50/10 dark:bg-slate-800/10' : ''}`}
                            >
                                <div className="flex flex-col items-center mb-2">
                                    <span className={`text-[8px] uppercase tracking-tighter ${isToday ? 'font-black text-brand-orange' : 'font-black text-slate-300 dark:text-slate-600'}`}>
                                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
                                    </span>
                                    <span className={`text-[11px] leading-none ${isToday ? 'font-black text-brand-orange' : 'font-black text-slate-800 dark:text-slate-200'}`}>
                                        {date.getDate()}
                                    </span>
                                </div>

                                <div className="flex-grow flex-col gap-1.5 items-center justify-center flex">
                                    <MealPill type="morning" count={mealCounts.breakfast} isActiveFilter={visibleMeals.includes('Breakfast')} />
                                    <MealPill type="noon" count={mealCounts.lunch} isActiveFilter={visibleMeals.includes('Lunch')} />
                                    <MealPill type="evening" count={mealCounts.dinner} isActiveFilter={visibleMeals.includes('Dinner')} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="grid grid-cols-7 divide-x divide-slate-50 dark:divide-slate-800 rtl:divide-x-reverse h-10 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800 animate-fade-in">
                    {week.map((date, dIdx) => {
                        const counts = getMealCounts(date);
                        const hasMeals = (counts.breakfast + counts.lunch + counts.dinner) > 0;
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isDifferentMonth = date.getMonth() !== currentMonth.getMonth();

                        return (
                            <div 
                                key={dIdx} 
                                className={`flex items-center justify-center relative ${isToday ? 'bg-amber-50/20 dark:bg-amber-900/10' : ''} ${isDifferentMonth ? 'opacity-20' : ''}`}
                            >
                                {hasMeals ? (
                                    <div className="w-2.5 h-2.5 rounded-full bg-brand-orange animate-pulse shadow-sm" />
                                ) : (
                                    <div className="w-2.5 h-2.5 rounded-full border border-slate-200 dark:border-slate-700" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MonthlyPlanner: React.FC<MonthlyPlannerProps> = ({ 
    viewMode, setViewMode, onInfoClick = () => {},
    activeHouseholdId, activeHousehold = null, recipes, language, onGoToWeek,
    role, householdRole, onAddRecipesToDay, onScheduleMeal, onRemoveRecipeFromDay, onMoveMeal, onUpdateMeal, onRecipeClick, onCookMeal,
    onUpdateMealSlotTitle,
    visibleMealTypes = ['Lunch', 'Dinner'], weekStartDay = 'Monday', activeCart, onUpdateActiveCart, onEditMeal, categoryOverrides = {},
    currentUserId = '', userMealPreferences = null, chefs = [], allUsers = [], onToggleFavoriteRecipe, onCreateRecipe,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const isHe = language === 'he';
    const canManage = role === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole);
    const cookingSessionStatuses = useCookingSessionStatuses(activeHouseholdId);
    
    const householdMealTypes = useMemo(
        () => normalizeVisibleMealTypes(visibleMealTypes),
        [visibleMealTypes],
    );
    const householdMealsKey = householdMealTypes.join(',');

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [allPlans, setAllPlans] = useState<Record<string, MealPlan>>({});
    const [loading, setLoading] = useState(true);
    const [visibleMeals, setVisibleMeals] = useState<MealType[]>(householdMealTypes);
    const [inspectingDay, setInspectingDay] = useState<{date: Date, weekOffset: number} | null>(null);
    const [displayMode, setDisplayMode] = useState<'stack' | 'calendar'>('calendar');
    
    const [bulkListWeeks, setBulkListWeeks] = useState<Date[][] | null>(null);
    const [selectorTarget, setSelectorTarget] = useState<{day: DayOfWeek, type: MealType, weekOffset: number} | null>(null);
    const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

    useEffect(() => {
        if (!activeHouseholdId) return;
        setLoading(true);
        const q = query(collection(db, 'householdPlans'), where('householdId', '==', activeHouseholdId));
        return onSnapshot(q, (snap: any) => {
            const plans: Record<string, MealPlan> = {};
            snap.docs.forEach((d: any) => {
                const plan = d.data() as MealPlan;
                if (plan.weekStartDate) plans[plan.weekStartDate] = plan;
            });
            setAllPlans(plans);
            setLoading(false);
        });
    }, [activeHouseholdId]);

    useEffect(() => {
        setVisibleMeals(householdMealTypes);
    }, [activeHouseholdId, householdMealsKey]);

    const weekBlocks = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startOfFirstWeek = getStartOfWeekLocal(firstDayOfMonth, weekStartDay as WeekStartDay);

        const weeks = [];
        let currentWeekStart = new Date(startOfFirstWeek);
        while (currentWeekStart <= lastDayOfMonth) {
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
                const dayDate = new Date(currentWeekStart);
                dayDate.setDate(currentWeekStart.getDate() + i);
                weekDays.push(dayDate);
            }
            weeks.push(weekDays);
            currentWeekStart = new Date(currentWeekStart);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }
        return weeks;
    }, [currentMonth, weekStartDay]);

    const getPlanForDate = (date: Date) => {
        const startOfWeek = getStartOfWeekLocal(date, weekStartDay as WeekStartDay);
        const weekKey = formatDateLocal(startOfWeek);
        const plan = allPlans[weekKey];
        if (!plan) return null;
        return plan[DAYS_OF_WEEK[date.getDay()]] || [];
    };

    const getMealPlanDocForDate = (date: Date): MealPlan | null => {
        const startOfWeek = getStartOfWeekLocal(date, weekStartDay as WeekStartDay);
        const weekKey = formatDateLocal(startOfWeek);
        return allPlans[weekKey] ?? null;
    };

    const getMealCounts = (date: Date) => {
        const dayMeals = getPlanForDate(date) || [];
        return {
            breakfast: dayMeals.filter(m => m.mealType === 'Breakfast').reduce((sum, m) => sum + m.plannedServings, 0),
            lunch: dayMeals.filter(m => m.mealType === 'Lunch').reduce((sum, m) => sum + m.plannedServings, 0),
            dinner: dayMeals.filter(m => m.mealType === 'Dinner' || !m.mealType).reduce((sum, m) => sum + m.plannedServings, 0)
        };
    };

    const toggleMealFilter = (type: MealType) => {
        if (!householdMealTypes.includes(type)) return;
        setVisibleMeals(prev => {
            if (prev.includes(type)) {
                return prev.length <= 1 ? prev : prev.filter(m => m !== type);
            }
            return MEAL_ORDER.filter(m => prev.includes(m) || m === type);
        });
    };

    const calculateWeekOffset = (date: Date) => {
        const now = new Date();
        const startOfThisWeek = getStartOfWeekLocal(now, weekStartDay as WeekStartDay);
        const startOfTargetWeek = getStartOfWeekLocal(date, weekStartDay as WeekStartDay);
        const diffTime = startOfTargetWeek.getTime() - startOfThisWeek.getTime();
        return Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));
    };

    const isCurrentYear = currentMonth.getFullYear() === new Date().getFullYear();
    const monthName = currentMonth.toLocaleString(isHe ? 'he-IL' : 'en-US', { 
        month: 'long', 
        ...(isCurrentYear ? {} : { year: 'numeric' as const })
    });

    const mealTypeLabel = (type: MealType) =>
        type === 'Breakfast' ? t('morning') : type === 'Lunch' ? t('noon') : t('evening');

    const weekDays = useMemo(() => {
        const days = weekStartDay === 'Sunday' 
            ? [0, 1, 2, 3, 4, 5, 6] 
            : [1, 2, 3, 4, 5, 6, 0];
        
        return days.map(d => {
            const date = new Date(2023, 0, 1 + d); // Jan 1 2023 was Sunday
            return date.toLocaleDateString(language, { weekday: 'short' });
        });
    }, [weekStartDay, language]);

    const { populatedDays, totalDays, recipeUsage, totalMeatWeight } = useMemo(() => {
        let pDays = 0;
        let tDays = 0;
        const usage: Record<string, { count: number, name: string, totalServings: number }> = {};

        const meatSum = calculateMonthlyMeat(weekBlocks, allPlans, recipes, currentMonth.getMonth());

        weekBlocks.forEach(week => {
            week.forEach(date => {
                if (date.getMonth() === currentMonth.getMonth()) {
                    tDays++;
                    const meals = getPlanForDate(date) || [];
                    
                    const activeMeals = meals.filter(m => householdMealTypes.includes(m.mealType || 'Dinner'));
                    if (activeMeals.length > 0) pDays++;

                    meals.forEach(m => {
                        const recipe = recipes.find(r => r.id === m.recipeId);
                        
                        if (!recipe || recipe.name === 'Unknown' || m.recipeName === 'Unknown') return;

                        const rName = recipe.name;
                        if (!usage[rName]) usage[rName] = { count: 0, name: rName, totalServings: 0 };
                        usage[rName].count++;
                        usage[rName].totalServings += m.plannedServings;
                    });
                }
            });
        });

        const sortedUsage = Object.values(usage).sort((a, b) => b.count - a.count);
        return { populatedDays: pDays, totalDays: tDays, recipeUsage: sortedUsage, totalMeatWeight: meatSum };
    }, [weekBlocks, currentMonth, allPlans, householdMealTypes, recipes]);

    const handlePrevDay = () => {
        if (!inspectingDay) return;
        const prev = new Date(inspectingDay.date);
        prev.setDate(prev.getDate() - 1);
        const offset = calculateWeekOffset(prev);
        setInspectingDay({ date: prev, weekOffset: offset });
    };

    const handleNextDay = () => {
        if (!inspectingDay) return;
        const next = new Date(inspectingDay.date);
        next.setDate(next.getDate() + 1);
        const offset = calculateWeekOffset(next);
        setInspectingDay({ date: next, weekOffset: offset });
    };

    return (
        <div className="flex flex-col flex-1 bg-slate-100 dark:bg-slate-950 h-full overflow-y-auto pb-32 animate-fade-in font-rubik relative transition-colors">
            <PlannerHeader
                viewMode={viewMode}
                setViewMode={setViewMode}
                dateLabel={monthName}
                onPrevDate={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() - 1); setCurrentMonth(d); }}
                onNextDate={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() + 1); setCurrentMonth(d); }}
                isCurrentPeriod={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                onReturnToCurrent={() => setCurrentMonth(new Date())}
                onSyncClick={() => setIsWeekSelectorOpen(true)}
                onInfoClick={onInfoClick}
                onMenuClick={() => setIsUsageModalOpen(true)}
                language={language}
            />

            <div className="p-4 space-y-4">
                <div className="flex justify-between items-center gap-4 mb-2">
                    <div className="flex gap-2 overflow-x-auto scrollbar-none flex-1">
                        {householdMealTypes.map(m => {
                            const active = visibleMeals.includes(m);
                            return (
                                <button 
                                    key={m}
                                    onClick={() => toggleMealFilter(m)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${active ? 'bg-brand-sage text-white border-brand-sage shadow-md' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 hover:border-brand-sage dark:hover:border-brand-sage hover:text-brand-sage dark:hover:text-brand-sage'}`}
                                >
                                    {t(m === 'Breakfast' ? 'morning' : m === 'Lunch' ? 'noon' : 'evening')}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl gap-0.5 shrink-0">
                        <button onClick={() => setDisplayMode('stack')} className={`p-1.5 rounded-lg transition-all ${displayMode === 'stack' ? 'bg-white dark:bg-slate-700 shadow-sm text-brand-orange' : 'text-slate-400 dark:text-slate-500'}`}><ListIcon className="w-4 h-4" /></button>
                        <button onClick={() => setDisplayMode('calendar')} className={`p-1.5 rounded-lg transition-all ${displayMode === 'calendar' ? 'bg-white dark:bg-slate-700 shadow-sm text-brand-orange' : 'text-slate-400 dark:text-slate-500'}`}><LayoutGridIcon className="w-4 h-4" /></button>
                    </div>
                </div>

                {displayMode === 'stack' ? (
                    weekBlocks.map((week, idx) => (
                        <WeekRow 
                            key={idx} 
                            week={week} 
                            index={idx} 
                            currentMonth={currentMonth}
                            calculateWeekOffset={calculateWeekOffset}
                            getMealCounts={getMealCounts}
                            setInspectingDay={(val) => setInspectingDay(val)}
                            onOpenShoppingList={(dates) => {
                                setBulkListWeeks([dates]);
                            }}
                            onViewWeek={onGoToWeek}
                            visibleMeals={visibleMeals}
                            t={t}
                        />
                    ))
                ) : (
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col animate-fade-in transition-colors">
                        {/* Header Days */}
                        <div className="grid grid-cols-[repeat(7,1fr)_3.5rem] border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            {weekDays.map((d, i) => (
                                <div key={i} className="py-3 text-center text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                    {d}
                                </div>
                            ))}
                            <div className="py-3 text-center text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                {/* Spacer for action column */}
                            </div>
                        </div>
                        
                        {/* Grid */}
                        <div className="grid grid-cols-[repeat(7,1fr)_3.5rem] auto-rows-fr bg-slate-50 dark:bg-slate-800 gap-px border-b border-slate-200 dark:border-slate-800">
                             {weekBlocks.map((week, wIdx) => {
                                 const offset = calculateWeekOffset(week[0]);
                                 return (
                                     <React.Fragment key={wIdx}>
                                         {week.map((date, dIdx) => {
                                             const isToday = date.toDateString() === new Date().toDateString();
                                             const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                                             const counts = getMealCounts(date);
                                             const dayOffset = calculateWeekOffset(date);
                                             
                                             return (
                                                 <div 
                                                    key={dIdx}
                                                    onClick={() => setInspectingDay({ date, weekOffset: dayOffset })}
                                                    className={`
                                                        min-h-[80px] bg-white dark:bg-slate-900 p-1 flex flex-col items-center cursor-pointer transition-colors hover:bg-sky-50 dark:hover:bg-slate-800
                                                        ${!isCurrentMonth ? 'bg-slate-50/50 dark:bg-slate-800/50 text-slate-300 dark:text-slate-600' : ''}
                                                    `}
                                                 >
                                                     <span className={`
                                                        text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full mb-1
                                                        ${isToday ? 'bg-brand-orange text-white shadow-sm' : (isCurrentMonth ? 'text-slate-700 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600')}
                                                     `}>
                                                         {date.getDate()}
                                                     </span>
                                                     
                                                     <div className="flex flex-col gap-1 w-full px-1">
                                                         {visibleMeals.includes('Breakfast') && (
                                                             <div className={`h-1.5 w-full rounded-full ${counts.breakfast > 0 ? 'bg-yellow-200 dark:bg-yellow-700' : 'bg-slate-100 dark:bg-slate-800'}`} />
                                                         )}
                                                         {visibleMeals.includes('Lunch') && (
                                                             <div className={`h-1.5 w-full rounded-full ${counts.lunch > 0 ? 'bg-orange-200 dark:bg-orange-700' : 'bg-slate-100 dark:bg-slate-800'}`} />
                                                         )}
                                                         {visibleMeals.includes('Dinner') && (
                                                             <div className={`h-1.5 w-full rounded-full ${counts.dinner > 0 ? 'bg-slate-600 dark:bg-slate-400' : 'bg-slate-100 dark:bg-slate-800'}`} />
                                                         )}
                                                     </div>
                                                 </div>
                                             );
                                         })}
                                         
                                         {/* Action Cell */}
                                         <div className="bg-white dark:bg-slate-900 flex items-center justify-center">
                                             <button 
                                                 onClick={() => onGoToWeek(offset)}
                                                 className="p-2 text-slate-300 dark:text-slate-600 hover:text-brand-orange dark:hover:text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-xl transition-all"
                                                 title={t('viewFullWeek')}
                                             >
                                                 <CalendarIcon className="w-5 h-5" />
                                             </button>
                                         </div>
                                     </React.Fragment>
                                 );
                             })}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <CompactWeekSelector
                isOpen={isWeekSelectorOpen}
                onClose={() => setIsWeekSelectorOpen(false)}
                onSync={(indices) => {
                    const selectedWeeks = indices.map(i => weekBlocks[i]);
                    setBulkListWeeks(selectedWeeks);
                    setIsWeekSelectorOpen(false);
                }}
                weeks={weekBlocks}
                mealPlans={allPlans}
                language={language}
                weekStartDay={weekStartDay}
            />

            {bulkListWeeks && (
                <BulkShoppingListModal 
                    isOpen={!!bulkListWeeks} 
                    onClose={() => setBulkListWeeks(null)}
                    onBack={() => {
                        setBulkListWeeks(null);
                        setIsWeekSelectorOpen(true);
                    }}
                    weeks={bulkListWeeks} 
                    recipes={recipes} 
                    language={language}
                    activeCart={activeCart}
                    onUpdateActiveCart={onUpdateActiveCart}
                    mealPlan={allPlans}
                    orderedDays={DAYS_OF_WEEK}
                    showWeekBar={false}
                    categoryOverrides={categoryOverrides}
                />
            )}

            <RecipeUsageModal
                isOpen={isUsageModalOpen}
                onClose={() => setIsUsageModalOpen(false)}
                usageData={recipeUsage}
                populatedDays={populatedDays}
                totalDays={totalDays}
                meatWeight={totalMeatWeight}
                titleKey="monthlyRecipeUsage"
                language={language}
            />

            {inspectingDay && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={() => setInspectingDay(null)}>
                    <div className="bg-slate-100 dark:bg-slate-950 w-full max-w-lg max-h-[85vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col border border-white/10 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                        <div className="p-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center sticky top-0 z-10">
                            <div className="flex items-center gap-1 sm:gap-3 flex-grow">
                                <button 
                                    onClick={handlePrevDay}
                                    className="p-2 text-slate-400 hover:text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-full transition-all active:scale-90"
                                >
                                    <ArrowLeftIcon className="w-6 h-6 rtl:rotate-180" />
                                </button>
                                
                                <h3 className="font-black text-slate-800 dark:text-slate-100 text-lg flex items-center justify-center gap-2 flex-grow text-center">
                                    <span className="hidden sm:inline"><CalendarIcon className="w-5 h-5 text-brand-orange" /></span>
                                    <span className="truncate">
                                        {inspectingDay.date.toLocaleDateString(language, { weekday: 'long', day: 'numeric', month: 'long' })}
                                    </span>
                                </h3>

                                <button 
                                    onClick={handleNextDay}
                                    className="p-2 text-slate-400 hover:text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-full transition-all active:scale-90"
                                >
                                    <ArrowLeftIcon className="w-6 h-6 rotate-180 rtl:rotate-0" />
                                </button>
                            </div>
                            
                            <button onClick={() => setInspectingDay(null)} className="p-2 -mr-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full">
                                <XIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="flex-grow overflow-y-auto p-4">
                            <DayCard 
                                day={DAYS_OF_WEEK[inspectingDay.date.getDay()]}
                                meals={(getPlanForDate(inspectingDay.date) || []).map(pm =>
                                    attachRecipeToPlannedMeal(pm, recipes, t('unknownRecipe')),
                                )}
                                role={role}
                                canManage={canManage}
                                onDropRecipe={(rid, type) => onAddRecipesToDay([rid], DAYS_OF_WEEK[inspectingDay.date.getDay()], type, inspectingDay.weekOffset)}
                                onRemoveRecipe={(pmId) => onRemoveRecipeFromDay(pmId, DAYS_OF_WEEK[inspectingDay.date.getDay()], inspectingDay.weekOffset)}
                                onMoveMeal={(pmId, fd, td, tt) => onMoveMeal(pmId, fd, td, tt, inspectingDay.weekOffset)}
                                onUpdateMeal={(pmId, up) => onUpdateMeal(pmId, DAYS_OF_WEEK[inspectingDay.date.getDay()], up, inspectingDay.weekOffset)}
                                onRecipeClick={onRecipeClick}
                                onAddMeal={(type) => setSelectorTarget({ day: DAYS_OF_WEEK[inspectingDay.date.getDay()], type, weekOffset: inspectingDay.weekOffset })}
                                onGenerateMealShoppingList={() => {}}
                                visibleMealTypes={householdMealTypes}
                                language={language}
                                onMealDragStart={() => {}}
                                onMealDragEnd={() => {}}
                                onCookMeal={(meals, type, initialRecipeId) => onCookMeal(
                                    meals,
                                    type,
                                    DAYS_OF_WEEK[inspectingDay.date.getDay()],
                                    inspectingDay.weekOffset,
                                    getMealPlanDocForDate(inspectingDay.date)?.slotMenuTitles,
                                    initialRecipeId,
                                )}
                                onEditMeal={(meal) => {
                                    if (onEditMeal) {
                                        onEditMeal(meal, DAYS_OF_WEEK[inspectingDay.date.getDay()], inspectingDay.weekOffset);
                                    }
                                }}
                                weekOffset={inspectingDay.weekOffset}
                                cookingSessionStatuses={cookingSessionStatuses}
                                slotMenuTitles={getMealPlanDocForDate(inspectingDay.date)?.slotMenuTitles}
                                onUpdateMealSlotTitle={(day, mealType, title) => {
                                    onUpdateMealSlotTitle?.(day, mealType, title, inspectingDay.weekOffset);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {selectorTarget && (
                <RecipeSelectorModal 
                    isOpen={!!selectorTarget}
                    onClose={() => setSelectorTarget(null)}
                    recipes={recipes}
                    title={`${t('add')} - ${mealTypeLabel(selectorTarget.type)}`}
                    targetDay={selectorTarget.day}
                    targetMealType={selectorTarget.type}
                    weekOffset={selectorTarget.weekOffset}
                    orderedDays={
                        weekStartDay === 'Sunday'
                            ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
                            : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
                    }
                    visibleMealTypes={householdMealTypes}
                    onConfirmSchedule={async (recipeId, day, type, servings, status, offset) => {
                        if (onScheduleMeal) {
                            await onScheduleMeal(recipeId, day, type, servings, status, offset);
                        } else {
                            onAddRecipesToDay([recipeId], day, type, offset);
                        }
                    }}
                    activeHouseholdId={activeHouseholdId}
                    language={language}
                    userMealPreferences={userMealPreferences}
                    currentUserId={currentUserId}
                    chefs={chefs}
                    allUsers={allUsers}
                    activeHousehold={activeHousehold}
                    userRole={role}
                    householdRole={householdRole}
                    onToggleFavoriteRecipe={onToggleFavoriteRecipe}
                    onCreateRecipe={onCreateRecipe}
                />
            )}
        </div>
    );
};

export default MonthlyPlanner;
